(ns app.front.view.public-records.applications
  (:require
    ["ol/proj" :as ol.proj]
    [app.domain.municipality.model :as municipality-model]
    [app.domain.property.model :as property-model]
    [app.domain.public-map.model :as public-map-model]
    [app.domain.util :as du]
    [app.front.root :as root]
    [app.front.ui :as ui]
    [app.front.ui.rich-text :as ui-rt]
    [app.front.ui.sortable-table :as sortable-table]
    [app.front.util :refer [for-frag]]
    [app.front.view.create-application.gis-search :as gis-search]
    [app.front.view.landing-page-subs :as landing-page-subs]
    [app.front.view.map-common.info-box :as map-common-info-box]
    [app.front.view.map-common.util :as map-common-util]
    [app.front.view.public-records.common :as pr-common]
    [app.front.view.public-records.ui :as prui]
    [app.front.view.workspace.map :as workspace-map]
    [clojure.set :as set]
    [clojure.string :as str]
    [core.front.carbon :as cc]
    [core.front.date-util :as date-util]
    [core.front.event :refer [<sub >evt]]
    [re-frame.core-instrumented :as rf]
    [reagent.core :as r]))

;; ---- Helper functions -------------------------------------------------------

(defn- base-search-options [db]
  (let [{:keys [config selected-options]} (::pr-common/data db)]
    (merge
      {:domain-group-id (-> config :domain-group :group/id)}
      (pr-common/selected-options->search-options selected-options))))

(defn- effective-permit-types
  "Computes effective permit types by intersecting user-selected permit types
   with tab permit types. Falls back to tab permit types if intersection is empty
   or if user hasn't selected any."
  [tab-permit-types user-permit-types]
  (or (not-empty (set/intersection tab-permit-types user-permit-types))
      tab-permit-types))

(defn- permit-type-options [available-filters tab-permit-types]
  (some-> (:permit-types available-filters)
          (set/intersection tab-permit-types)
          not-empty))

(defn- visible-filter-configs [{:keys [config tab-permit-types]} available-filters]
  [{:attribute          :permit-types
    :visibility-options (permit-type-options available-filters tab-permit-types)}
   {:attribute          :categories
    :visibility-options (pr-common/available-category-options available-filters (:permit-types config))}
   {:attribute          :municipalities
    :visibility-options (:municipalities available-filters)}])

(defn- search-applications-command-data [db]
  (let [{:keys [limit skip sort-condition
                tab-permit-types
                selected-property
                selected-view
                extent-polygon]}          (::pr-common/data db)
        map-view?                         (= selected-view :dashboard-search/map-view)
        ;; The selected property owns the scope when there is one; the viewport
        ;; extent only exists in map view, so the list simply gets no extent.
        scope                             (pr-common/search-scope selected-property
                                                                  (when map-view? extent-polygon))
        base                              (base-search-options db)
        effective-pts                     (effective-permit-types tab-permit-types (:permit-types base))
        ;; The unpaginated limit exists so that every record in the searched area
        ;; reaches the map. It is the scope that bounds it, so tie it to the scope
        ;; rather than to the view — before the first extent arrives there is
        ;; nothing to bound it and the paginated window is the safe answer.
        unpaginated?                      (boolean (and map-view? scope))]
    (cond-> (assoc base :permit-types effective-pts)
      unpaginated?       (assoc :limit pr-common/unpaginated-search-limit)
      (not unpaginated?) (assoc :skip skip
                                :limit limit)
      :always            (pr-common/assoc-search-scope scope)
      sort-condition     (assoc :sort-condition sort-condition))))

(def application-selection-keys
  {:search-results-key           :application-search-results
   :selected-property-key        :selected-property
   :selected-result-key          :selected-result
   :selected-cluster-results-key :selected-cluster-results
   :explicit-map-center-key      :explicit-map-center})

(def secondary-complaint-selection-keys
  {:search-results-key           :secondary-complaint-search-results
   :selected-result-key          :secondary-complaint-selected-result
   :selected-cluster-results-key :selected-cluster-results})

;; ---- Events -----------------------------------------------------------------

(rf/reg-event-fx ::search
  (fn [{:keys [db]} [_ {:keys [keep-results? sort-condition from-map-move?]}]]
    (let [data-db (::pr-common/data db)]
      ;; Ignore a debounced map search that lands after leaving map view.
      (when (or (not from-map-move?)
                (= :dashboard-search/map-view (:selected-view data-db)))
        (let [actual-sort        (or sort-condition (:sort-condition data-db))
              new-skip           (pr-common/search-skip (:skip data-db) keep-results?)
              request-generation (inc (or (:application-search-generation data-db) 0))
              updated-data-db    (cond-> (assoc data-db
                                                :skip new-skip
                                                :application-search-generation request-generation
                                                :application-loading-more? (boolean keep-results?))
                                   actual-sort (assoc :sort-condition actual-sort))
              updated-db         (assoc db ::pr-common/data updated-data-db)]
          {:db updated-db
           :fx [[:dispatch [:comm/command-monitored
                            {:command    :public-records/search-applications
                             :data       (search-applications-command-data updated-db)
                             :on-success [::search.success {:keep-results?      keep-results?
                                                            :from-map-move?     from-map-move?
                                                            :request-generation request-generation}]}]]]})))))

(defn- secondary-search-refresh-fx [{:keys [include-secondary-map-records?
                                            selected-view
                                            extent-polygon]}]
  (when (and include-secondary-map-records?
             (= selected-view :dashboard-search/map-view)
             extent-polygon)
    [[:dispatch [::search-secondary-complaints-for-map]]]))

(rf/reg-event-fx ::search.success
  pr-common/data-path
  (pr-common/make-search-success-handler
    {:total-count-key           :total-count
     :available-filters-key     :available-filters
     :selected-options-key      :selected-options
     :search-results-key        :application-search-results
     :visible-filters-key       :visible-filters
     :visible-filter-configs-fn visible-filter-configs
     :search-evt                ::search
     :secondary-refresh-fx-fn   secondary-search-refresh-fx
     :from-map-move-flag-key    :search-results-from-map-move?
     :request-generation-key    :application-search-generation}))

(rf/reg-event-fx ::load-more
  pr-common/data-path
  (pr-common/make-load-more-handler
    {:search-evt ::search}))

(rf/reg-event-fx ::set-selected-options
  pr-common/data-path
  (fn [{:keys [db]} [_ attribute options]]
    (let [new-db (-> (assoc-in db [:selected-options attribute] options)
                     (dissoc :selected-cluster-results))]
      {:db new-db
       :fx (into [[:dispatch [::search {:keep-results? false}]]]
                 (secondary-search-refresh-fx new-db))})))

(rf/reg-event-fx ::set-selected-view
  pr-common/data-path
  (fn [{:keys [db]} [_ view]]
    (let [new-db    (-> (assoc db :selected-view view)
                        (dissoc :selected-cluster-results))
          map-view? (= view :dashboard-search/map-view)]
      {:db new-db
       :fx (cond-> []
             ;; Postrender triggers the first extent search after entering map view.
             (or (not map-view?) (:extent-polygon new-db))
             (conj [:dispatch [::search {:keep-results? false}]])

             :always
             (into (secondary-search-refresh-fx new-db)))})))

(rf/reg-event-fx ::map-moved
  pr-common/data-path
  (fn [{:keys [db]} [_ ^js ol-map]]
    (when (= :dashboard-search/map-view (:selected-view db))
      (let [{:keys [include-secondary-map-records? selected-property]} db]
        (cond-> {:db (assoc db :extent-polygon (map-common-util/map-extent->polygon ol-map))}
          (not selected-property)
          ;; Movement-driven results must not fit the map and trigger another moveend.
          (assoc :dispatch-debounce
                 (cond-> [{:id      ::search-for-map
                           :timeout 300
                           :action  :dispatch
                           :event   [::search {:keep-results?  false
                                               :from-map-move? true}]}]
                   include-secondary-map-records?
                   (conj {:id      ::search-secondary-complaints-for-map
                          :timeout 300
                          :action  :dispatch
                          :event   [::search-secondary-complaints-for-map]}))))))))

(rf/reg-event-fx ::search-secondary-complaints-for-map
  (fn [{:keys [db]} _]
    (let [{:keys [extent-polygon include-secondary-map-records?
                  selected-property selected-view]}             (::pr-common/data db)]
      (if (and include-secondary-map-records? extent-polygon)
        (when (= selected-view :dashboard-search/map-view)
          {:fx [[:dispatch [:comm/command-monitored
                            {:command    :public-records/search-complaints
                             :data       (-> db
                                             base-search-options
                                             (pr-common/assoc-search-scope
                                               (pr-common/search-scope selected-property extent-polygon))
                                             (assoc :limit pr-common/unpaginated-search-limit))
                             :on-success [::search-secondary-complaints-for-map.success]}]]]})
        {:db (update db ::pr-common/data dissoc
                     :secondary-complaint-search-results
                     :secondary-complaint-total-count
                     :secondary-complaint-selected-result)}))))

(rf/reg-event-db ::search-secondary-complaints-for-map.success
  pr-common/data-path
  (fn [db [_ _ response]]
    (cond-> db
      (and (:include-secondary-map-records? db)
           (= (pr-common/tab-idx->tab (:tabs db) (:selected-tab db))
              :public-records/cases))
      (assoc :secondary-complaint-search-results (:results response)
             :secondary-complaint-total-count (:total-count response)))))

(rf/reg-event-fx ::map-click
  (pr-common/make-map-click-handler
    {:single-feature-fx    (fn [db js-feature]
                             (pr-common/select-combined-single-feature-fx db
                                                                          js-feature
                                                                          application-selection-keys
                                                                          secondary-complaint-selection-keys))
     :cluster-results-fx   (fn [db features]
                             (if (get-in db [::pr-common/data :include-secondary-map-records?])
                               (pr-common/select-combined-cluster-results-fx db
                                                                             features
                                                                             application-selection-keys
                                                                             secondary-complaint-selection-keys)
                               (pr-common/select-cluster-results-fx db features application-selection-keys)))
     :search-success-evt   ::map-click.success
     :search-not-found-evt ::map-click.not-found
     :layer-id-fn          (fn [db]
                             [(if (get-in db [::pr-common/data :include-secondary-map-records?])
                                pr-common/combined-results-layer-id
                                pr-common/results-layer-id)])}))

(rf/reg-event-fx ::map-click.success
  pr-common/data-path
  (fn [{:keys [db]} [_ click-coordinates _ feature]]
    (let [selected-property (pr-common/gis-data->selected-property feature)
          search-value      (or (-> feature :address :address/full-address) "")
          new-db            (-> db
                                (assoc :explicit-map-center click-coordinates
                                       :selected-property selected-property)
                                (dissoc :selected-result
                                        :secondary-complaint-selected-result
                                        :selected-cluster-results))]
      {:db new-db
       :fx (into [[:dispatch [::gis-search/set-search-value search-value]]
                  [:dispatch [::search {:keep-results? false}]]]
                 (secondary-search-refresh-fx new-db))})))

(rf/reg-event-fx ::map-click.not-found
  pr-common/data-path
  (fn [{:keys [db]} _]
    (let [new-db (dissoc db
                         :selected-property
                         :complaint-selected-property
                         :selected-result
                         :secondary-complaint-selected-result
                         :selected-cluster-results)]
      {:db new-db
       :fx (cond-> [[:dispatch [::gis-search/set-search-value ""]]]
             (:selected-property db)
             (-> (conj [:dispatch [::search {:keep-results? false}]])
                 (into (secondary-search-refresh-fx new-db))))})))

(rf/reg-event-fx ::select-property
  pr-common/data-path
  (fn [{:keys [db]} [_ feature]]
    (let [new-db (-> (pr-common/select-property-db db feature application-selection-keys)
                     (dissoc :secondary-complaint-selected-result))]
      {:db new-db
       :fx (into [[:dispatch [::search {:keep-results? false}]]]
                 (secondary-search-refresh-fx new-db))})))

(rf/reg-event-db ::set-selected-result
  pr-common/data-path
  (fn [db [_ workspace]]
    (-> db
        (pr-common/set-selected-result-db workspace (:selected-result-key application-selection-keys))
        (dissoc :secondary-complaint-selected-result))))

(rf/reg-event-db ::set-secondary-complaint-selected-result
  pr-common/data-path
  (fn [db [_ complaint]]
    (-> db
        (assoc :secondary-complaint-selected-result complaint)
        (dissoc :selected-result))))

(rf/reg-event-fx ::close-selected-property-view
  pr-common/data-path
  (fn [{:keys [db]} _]
    (let [new-db (-> db
                     (pr-common/close-selected-property-view-db application-selection-keys)
                     (dissoc :complaint-selected-property
                             :secondary-complaint-selected-result))]
      {:db new-db
       :fx (cond-> []
             (:selected-property db)
             (-> (conj [:dispatch [::search {:keep-results? false}]])
                 (into (secondary-search-refresh-fx new-db))))})))

(rf/reg-event-db ::close-cluster-view
  pr-common/data-path
  (fn [db _]
    (pr-common/close-cluster-view-db db application-selection-keys)))

(rf/reg-event-db ::close-selected-result-view
  pr-common/data-path
  (fn [db _]
    (-> db
        (pr-common/close-selected-result-db application-selection-keys)
        (dissoc :secondary-complaint-selected-result))))

(rf/reg-event-fx ::clear-selected-property
  (fn [{:keys [db]} _]
    (let [new-db (update db ::pr-common/data
                         #(-> %
                              (pr-common/close-selected-property-view-db application-selection-keys)
                              (dissoc :complaint-selected-property)))]
      {:db new-db
       :fx (into [[:dispatch [::search {:keep-results? false}]]]
                 (secondary-search-refresh-fx (::pr-common/data new-db)))})))

(rf/reg-event-db ::show-on-map
  pr-common/data-path
  (fn [db [_ workspace]]
    (let [map-center (some-> workspace :location :coordinates clj->js ol.proj/fromLonLat js->clj)]
      (-> db
          (assoc :selected-view :dashboard-search/map-view
                 :selected-result workspace
                 :explicit-map-center map-center)
          (dissoc :selected-cluster-results)))))

;; ---- Subscriptions ----------------------------------------------------------

(rf/reg-sub ::search-results
  :<- [::pr-common/data]
  :-> :application-search-results)

(rf/reg-sub ::total-count
  :<- [::pr-common/data]
  (fn [{:keys [total-count
               secondary-complaint-total-count
               include-secondary-map-records?
               selected-view]}]
    (if (and include-secondary-map-records?
             (= selected-view :dashboard-search/map-view))
      (+ (or total-count 0) (or secondary-complaint-total-count 0))
      total-count)))

(rf/reg-sub ::available-filters
  :<- [::pr-common/data]
  :-> :available-filters)

(rf/reg-sub ::permit-types
  :<- [::available-filters]
  :<- [::pr-common/data]
  (fn [[available-filters data]]
    (permit-type-options available-filters (:tab-permit-types data))))

(rf/reg-sub ::categories
  :<- [::available-filters]
  :<- [::pr-common/permit-type-configs]
  :<- [:language]
  (fn [[available-filters permit-type-configs lang]]
    (pr-common/sorted-category-options available-filters permit-type-configs lang)))

(rf/reg-sub ::municipality-options
  :<- [::available-filters]
  :<- [::pr-common/municipalities]
  (pr-common/make-municipality-options-sub))

(rf/reg-sub ::configured-public-map-attributes
  :<- [::pr-common/permit-type-configs]
  (fn [permit-type-configs [_ workspace]]
    (pr-common/configured-public-map-attributes workspace permit-type-configs)))

(rf/reg-sub ::formatted-public-map-attributes
  (fn [[_ workspace]]
    [(rf/subscribe [::configured-public-map-attributes workspace])
     (rf/subscribe [::root/permit-types-tr [(pr-common/result-permit-type workspace)]])
     (rf/subscribe [:language])
     (rf/subscribe [::date-util/date-time-fn])])
  (fn [[attrs tr lang format-date-time] [_ workspace]]
    (->> attrs
         (sort-by #(tr (public-map-model/public-map-attribute-tr-key %)))
         (keep (fn [attribute]
                 (when-some [value (pr-common/format-attribute-value {:tr               tr
                                                                      :lang             lang
                                                                      :attribute        attribute
                                                                      :entity           workspace
                                                                      :format-date-time format-date-time})]
                   [(tr (public-map-model/public-map-attribute-tr-key attribute))
                    value]))))))

(rf/reg-sub ::selected-options
  :<- [::pr-common/data]
  (fn [data [_ attribute]]
    (-> data :selected-options attribute set)))

(rf/reg-sub ::visible-filters
  :<- [::pr-common/data]
  (fn [data _]
    (or (:visible-filters data) #{})))

(rf/reg-sub ::columns
  :<- [::municipality-options]
  :<- [::current-tab]
  :<- [:tr]
  :<- [:language]
  :<- [::date-util/date-fn]
  :<- [::pr-common/permit-type-names]
  (fn [[municipalities current-tab tr lang format-date permit-type-names] _]
    (let [municipality-number->name (->> municipalities
                                         (map (fn [m]
                                                [(:municipality/number m)
                                                 (municipality-model/municipality-name m lang)]))
                                         (into {}))
          development?              (= current-tab :public-records/development)
          cases?                    (= current-tab :public-records/cases)
          work-orders?              (= current-tab :public-records/work-orders)
          pi?                       (= current-tab :public-records/periodic-inspections)
          cases-or-wo?              (or cases? work-orders?)
          submitted-sort-value      (fn [workspace]
                                      (some-> (or (:submitted workspace)
                                                  (:created workspace))
                                              .getTime))
          submitted-extractor       (fn [workspace]
                                      (some-> (or (:submitted workspace)
                                                  (:created workspace))
                                              format-date))]
      (into []
            (concat
              (when (du/at-least-two? municipalities)
                [[(tr [:public-records/municipality])
                  #(->> % :municipality/number (get municipality-number->name))]])
              (when development?
                [[(tr [:public-records/permit-type])
                  #(get permit-type-names (pr-common/result-permit-type %))]])
              [[(tr [:public-records/record-number])
                :workspace/municipal-case-id
                {:sorting            (fn [a b]
                                       (compare (:workspace/municipal-case-id a "")
                                                (:workspace/municipal-case-id b "")))
                 :sort-attribute-key :workspace/municipal-case-id
                 :on-sort-hook       (partial pr-common/on-sort-hook ::search :workspace/municipal-case-id)}]]
              (when-not pi?
                [[(tr [:workspace/address])
                  :address/full-address]])
              [[(tr [:public-records/category])
                #(pr-common/category-name % lang)]]
              (when cases-or-wo?
                [[(tr [:public-records/description])
                  (fn [workspace]
                    (let [id (str "case-description-text-editor" (:id workspace))]
                      ^{:key id}
                      [ui-rt/editor-legacy {:initial-value (:workspace/case-description workspace)
                                            :id            id}]))]])
              (when development?
                [[(tr [:public-records/submitted])
                  submitted-extractor
                  {:sorting            (fn [a b]
                                         (compare (submitted-sort-value a)
                                                  (submitted-sort-value b)))
                   :sort-attribute-key :submitted
                   :on-sort-hook       (partial pr-common/on-sort-hook ::search :submitted)}]])
              (when (or cases-or-wo? pi?)
                [[(tr [:public-records/created])
                  #(some-> % :created format-date)
                  {:sorting            (fn [a b]
                                         (compare (:created a) (:created b)))
                   :sort-attribute-key :created
                   :on-sort-hook       (partial pr-common/on-sort-hook ::search :created)}]])
              (when pi?
                [[(tr [:public-records/state])
                  #(tr [(:domain/state %)])]
                 [(tr [:public-map-attributes/applicants])
                  #(str/join ", " (:applicants %))]
                 [(tr [:public-map-attributes/property-owners])
                  #(str/join ", " (:property-owners %))]]))))))

(rf/reg-sub ::selected-view
  :<- [::pr-common/data]
  :-> :selected-view)

(rf/reg-sub ::loading-more?
  :<- [::pr-common/data]
  :-> :application-loading-more?)

(rf/reg-sub ::current-tab
  :<- [::pr-common/data]
  :-> :current-application-tab)

(rf/reg-sub ::sort-condition
  :<- [::pr-common/data]
  :-> :sort-condition)

(rf/reg-sub ::initial-sorting
  :<- [::sort-condition]
  :<- [::columns]
  (fn [[sort-condition columns] _]
    (pr-common/sort-condition->initial-sorting sort-condition columns)))

(rf/reg-sub ::explicit-map-center
  :<- [::pr-common/data]
  :-> :explicit-map-center)

(rf/reg-sub ::map-center
  :<- [::explicit-map-center]
  :<- [::selected-options :municipalities]
  :<- [::pr-common/municipalities]
  (fn [[explicit-center selected-municipalities municipalities]]
    (or explicit-center
        (->> (or (not-empty selected-municipalities) municipalities)
             (keep :municipality/center-location)
             municipality-model/center-of-centers))))

(rf/reg-sub ::selected-or-all-municipality-numbers
  :<- [::pr-common/municipalities]
  :<- [::selected-options :municipalities]
  (fn [[municipalities selected-municipalities] _]
    (mapv :municipality/number
          (or (not-empty selected-municipalities)
              municipalities))))

(rf/reg-sub ::selected-property
  :<- [::pr-common/data]
  :-> :selected-property)

(rf/reg-sub ::primary-selected-result
  :<- [::pr-common/data]
  :-> :selected-result)

(rf/reg-sub ::selected-cluster-results
  :<- [::pr-common/data]
  :-> :selected-cluster-results)

(rf/reg-sub ::selected-cluster-ids
  :<- [::selected-cluster-results]
  (fn [results _]
    (set (map :id results))))

(rf/reg-sub ::secondary-complaint-selected-result
  :<- [::pr-common/data]
  :-> :secondary-complaint-selected-result)

(rf/reg-sub ::selected-result
  :<- [::primary-selected-result]
  :<- [::secondary-complaint-selected-result]
  (fn [[selected-result secondary-complaint-selected-result] _]
    (or selected-result
        secondary-complaint-selected-result)))

(rf/reg-sub ::property-results
  :<- [::search-results]
  :<- [::secondary-complaint-search-results]
  (fn [[applications complaints] _]
    (vec (concat applications complaints))))

(rf/reg-sub ::secondary-complaint-search-results
  :<- [::pr-common/data]
  :-> :secondary-complaint-search-results)

(rf/reg-sub ::search-results-layer
  :<- [::search-results]
  :<- [::primary-selected-result]
  :<- [::selected-cluster-ids]
  :<- [:tr]
  (fn [[search-results selected-result selected-cluster-ids tr] _]
    (->> (pr-common/search-results->feature-collection search-results
                                                       selected-result
                                                       selected-cluster-ids)
         (pr-common/feature-collection->map-layer tr))))

(rf/reg-sub ::combined-search-results-layer
  :<- [::search-results]
  :<- [::primary-selected-result]
  :<- [::secondary-complaint-search-results]
  :<- [::secondary-complaint-selected-result]
  :<- [::selected-cluster-ids]
  :<- [:tr]
  (fn [[primary-results primary-selected-result secondary-results secondary-selected-result selected-cluster-ids tr] _]
    (let [combined-results         (concat primary-results secondary-results)
          combined-selected-id     (:id (or primary-selected-result secondary-selected-result))
          combined-selected-result (when combined-selected-id
                                     {:id combined-selected-id})]
      (->> (pr-common/search-results->feature-collection combined-results
                                                         combined-selected-result
                                                         selected-cluster-ids)
           (pr-common/feature-collection->map-layer pr-common/combined-results-layer-id
                                                    (tr [:public-records/public-records]))))))


(defn- search-filter [tr]
  (let [lang              (<sub [:language])
        visible-filters   (<sub [::visible-filters])
        permit-type-names (<sub [::pr-common/permit-type-names])]
    [pr-common/render-search-filters tr visible-filters
     (pr-common/search-filter-configs
       {:id-prefix            ""
        :selected-options     {:municipalities (<sub [::selected-options :municipalities])
                               :permit-types   (<sub [::selected-options :permit-types])
                               :categories     (<sub [::selected-options :categories])}
        :set-options-fn       #(>evt [::set-selected-options %1 %2])
        :lang                 lang
        :permit-type-names    permit-type-names
        :municipality-options (<sub [::municipality-options])
        :permit-type-options  (<sub [::permit-types])
        :category-options     (<sub [::categories])})]))

(defn- expanded-row-actions
  "Action row of an expanded search result. Show on map is offered only for
   records that have a location — contact-request-enabled? keeps the row itself
   visible for the contact request button, it must not lift the location guard."
  [tr workspace contact-request-enabled?]
  (when (or (:location workspace) contact-request-enabled?)
    [:div.mt-8.flex.flex-wrap.gap-2
     (when (:location workspace)
       [ui/button
        {:kind     :primary
         :size     :small
         :on-click #(>evt [::show-on-map workspace])}
        (tr [:public-records/show-on-map])])
     [pr-common/contact-request-button
      {:tr                tr
       :municipal-case-id (:workspace/municipal-case-id workspace)}]]))

(defn- search-result-table [tr]
  [sortable-table/table
   {:id              "application-search-results"
    :initial-sorting (<sub [::initial-sorting])
    :cols            (<sub [::columns])
    :configurable?   true
    :id-func         :id
    :items           (<sub [::search-results])
    :tr              tr
    :sticky-header?  true
    :expanded-func   (fn [workspace]
                       (let [attrs (<sub [::formatted-public-map-attributes workspace])]
                         [:div.p-4
                          [:div.grid.grid-cols-1.sm:grid-cols-2.lg:grid-cols-3.xl:grid-cols-4.gap-4
                           (for-frag [[label v] attrs]
                             [:div.p-2.bg-ui-light-gray.rounded
                              [:div.font-bold.text-sm.mb-1
                               label]
                              [:div.text-gray
                               v]])]
                          [expanded-row-actions
                           tr
                           workspace
                           (<sub [::landing-page-subs/contact-request-enabled?])]]))}])

(defn- list-view [tr in-progress?]
  [pr-common/render-paginated-table
   {:tr            tr
    :in-progress?  in-progress?
    :loading-more? (<sub [::loading-more?])
    :results       (<sub [::search-results])
    :total-count   (<sub [::total-count])
    :table         [search-result-table tr]
    :load-more-evt ::load-more}])

(defn- property-result-item [tr lang result]
  (let [complaint?       (= (:entity/type result) :entity.type/complaint)
        permit-type      (pr-common/result-permit-type result)
        permit-type-name (when permit-type
                           (<sub [::pr-common/permit-type-name permit-type]))]
    [:div.w-full
     [:button.p-3.bg-white.mt-2.w-full.border-0.text-left.select-search-result-button
      {:on-click #(>evt [(if complaint?
                           ::set-secondary-complaint-selected-result
                           ::set-selected-result)
                         result])}
      (when permit-type-name
        [:span.font-bold permit-type-name])
      (when-some [case-id (:workspace/municipal-case-id result)]
        [:span.text-gray-800 (when permit-type-name ", ") case-id])
      (when (:category-names result)
        [:span.text-gray-800
         (when (or permit-type-name (:workspace/municipal-case-id result)) ", ")
         (pr-common/category-name result lang)])
      (when complaint?
        [:span.text-gray-800
         (when (or permit-type-name
                   (:workspace/municipal-case-id result)
                   (:category-names result))
           ", ")
         (tr [:public-records/complaints])])]]))

(defn- selected-property-view [selected-property]
  (let [tr                (<sub [:tr])
        lang              (<sub [:language])
        property-id       (-> selected-property :property :property/id)
        results           (<sub [::property-results])
        address           (-> selected-property :address :address/full-address)
        municipality-name (or (<sub [::pr-common/municipality-name-by-number (:municipality selected-property)])
                              (<sub [::pr-common/single-municipality-name]))]
    [map-common-info-box/right-side-box
     {:on-cancel-click #(>evt [::close-selected-property-view])
      :tr              tr}
     [:div.flex-grow.overflow-y-auto.px-2.pb-2
      [:h3.font-bold.text-lg.mb-2
       (or address (tr [:address/unknown-address]))
       (when municipality-name
         (str ", " municipality-name))]
      (when property-id
        [:div.text-sm.text-gray-800.mb-3 property-id])
      (cond
        (or (<sub [:comm/command-processing? :public-records/search-applications])
            (<sub [:comm/command-processing? :public-records/search-complaints]))
        [:div.flex.justify-center.py-4
         (cc/loading-spinner {:with-overlay false :small true})]

        (seq results)
        (for-frag [workspace (sort-by :id results)]
          [property-result-item tr lang workspace])

        :else
        [:div.text-gray-800.py-4
         (tr [:public-records/no-results-found])])]]))

(defn- selected-cluster-view [results]
  (let [tr                (<sub [:tr])
        lang              (<sub [:language])
        selected-property (<sub [::selected-property])]
    [pr-common/cluster-results-view
     {:tr              tr
      :results         results
      ;; × resets the whole right-hand side, ← peels off just this layer — and
      ;; only when there is one underneath to return to. Same split as
      ;; selected-result-view below.
      :on-back-click   (when selected-property
                         #(>evt [::close-cluster-view]))
      :on-cancel-click #(>evt [::close-selected-property-view])
      :render-item     (fn [result] [property-result-item tr lang result])}]))

(defn- selected-result-view [{:keys [:entity/type
                                     :municipality/number
                                     :address/full-address
                                     location]
                              :as   selected-result}]
  (let [permit-type                    (pr-common/result-permit-type selected-result)
        tr                             (<sub [::root/permit-types-tr [permit-type]])
        lang                           (<sub [:language])
        format-date-time               (<sub [::date-util/date-fn])
        permit-type-name               (<sub [::pr-common/permit-type-name permit-type])
        selected-property              (<sub [::selected-property])
        selected-cluster-results       (<sub [::selected-cluster-results])
        municipality-name              (<sub [::pr-common/municipality-name-by-number number])
        include-secondary-map-records? (:include-secondary-map-records? (<sub [::pr-common/data]))
        workspace-attrs                (<sub [::formatted-public-map-attributes selected-result])
        fmt-opts                       {:tr tr :lang lang :entity selected-result :format-date-time format-date-time}
        complaint-attrs                (pr-common/format-detail-attrs fmt-opts
                                                                      [[:category-names     :public-records/category]
                                                                       [:created            :public-records/created]
                                                                       [:domain/state       :complaint/state]
                                                                       [:complaint/severity :complaint/severity]])
        attrs                          (if (= type :entity.type/complaint)
                                         complaint-attrs
                                         workspace-attrs)]
    [map-common-info-box/right-side-box
     {:on-cancel-click #(>evt [::close-selected-property-view])
      ;; × resets the whole right-hand side, ← peels off just this layer — and
      ;; only when there is one underneath to return to. Same split as the
      ;; complaints dashboard map (complaint.dashboard/complaint-view).
      :on-back-click   (when (or selected-cluster-results selected-property)
                         #(>evt [::close-selected-result-view]))
      :tr              tr}
     [:div.flex-grow.flex.flex-col.justify-between.items-start.overflow-y-auto.px-2.pb-2
      [:div
       [:h3.font-bold.text-lg.mb-2
        (or full-address
            (pr-common/location-coordinates location)
            (tr [:address/unknown-address]))
        (when municipality-name
          (str ", " municipality-name))]
       (when-some [property-id (-> selected-property :property :property/id)]
         [:div.text-sm.text-gray-800.mb-3 property-id])
       (when permit-type-name
         [:div.uppercase.text-xs.font-bold.mb-3
          permit-type-name])
       [:div.flex.flex-col.flex-1.gap-3.justify-start
        (into [:<>]
              (map (fn [[label value]]
                     [prui/form-row label value]))
              (cond-> []
                include-secondary-map-records?
                (conj [(tr [:public-records/record-type])
                       (pr-common/record-type-label tr type)])

                :always
                (into attrs)))]]
      [pr-common/contact-request-button {:tr                tr
                                         :kind              :secondary
                                         :municipal-case-id (:workspace/municipal-case-id selected-result)
                                         :class-name        "mt-3"}]]]))

(defn- fit-map-to-results [^js ol-map layer-id]
  (when-let [layer (->> (.getArray (.getLayers (.getLayerGroup ol-map)))
                        (filter #(= (.get % "id") layer-id))
                        first)]
    (let [cluster-source (.getSource ^js layer)
          vector-source  (.getSource ^js cluster-source)]
      (map-common-util/fit-map vector-source (.getView ol-map) {:max-zoom 15}))))

(defn- should-fit-results?
  "True when the map should fit the view to a fresh result set. Movement-driven
   results never fit — fitting would fire moveend again and the two would feed
   back into each other."
  [search-results last-fit-results from-map-move?]
  (and (seq search-results)
       (not= search-results last-fit-results)
       (not from-map-move?)))

(defn search-result-map []
  (r/with-let [*ol-map             (r/atom nil)
               *post-render-fired? (r/atom false)
               *last-fit-results   (r/atom nil)]
    (let [map-center                     (<sub [::map-center])
          gis-map-layers                 (<sub [::pr-common/gis-map-layers])
          primary-layers                 (into (vec gis-map-layers) (<sub [::search-results-layer]))
          combined-layers                (into (vec gis-map-layers) (<sub [::combined-search-results-layer]))
          data                           (<sub [::pr-common/data])
          include-secondary-map-records? (:include-secondary-map-records? data)
          domain-group-id                (<sub [::pr-common/domain-group-id])
          selected-property              (<sub [::selected-property])
          selected-result                (<sub [::selected-result])
          selected-cluster-results       (<sub [::selected-cluster-results])
          point-search-loading?          (<sub [::map-common-util/point-search-loading?])
          search-results                 (<sub [::search-results])
          layers                         (if include-secondary-map-records?
                                           combined-layers
                                           primary-layers)
          from-map-move?                 (:search-results-from-map-move? data)]
      (when @*ol-map
        (if (should-fit-results? search-results @*last-fit-results from-map-move?)
          (do (reset! *last-fit-results search-results)
              (fit-map-to-results @*ol-map (if include-secondary-map-records?
                                             pr-common/combined-results-layer-id
                                             pr-common/results-layer-id)))
          (when from-map-move?
            ;; The map has moved since the last fit — don't let a stale memo
            ;; suppress the next user-initiated fit of the same result set.
            (reset! *last-fit-results nil))))
      (when map-center
        [:div.flex.items-center.flex-wrap.lg:flex-nowrap
         [:div.flex.map-boundary.map-extended.bg-gray-400.rounded.overflow-hidden.h-full.w-full.mt-6
          {:class (when (or point-search-loading? selected-property selected-result selected-cluster-results)
                    ["lg:w-1-2" "lg:mr-2"])
           :style {:height "500px"}}
          [workspace-map/workspace-map
           {:center             map-center
            :layers             layers
            :extended?          true
            :primaryMapMarkers  []
            :events             [{:eventType     "singleclick"
                                  :eventFunction #(>evt [::map-click
                                                         %
                                                         {:group/id domain-group-id}])}
                                 {:eventType     "moveend"
                                  :eventFunction (fn [e]
                                                   (reset! *ol-map (.-map ^js e))
                                                   (>evt [::map-moved (.-map ^js e)]))}
                                 {:eventType     "postrender"
                                  :eventFunction (fn [e]
                                                   ;; postrender fires hundreds of times; capture the map
                                                   ;; reference and fire ::map-moved once to initialise
                                                   ;; the extent-polygon for secondary complaints.
                                                   ;; moveend does not fire on initial render without this.
                                                   (when-not @*post-render-fired?
                                                     (reset! *post-render-fired? true)
                                                     (reset! *ol-map (.-map ^js e))
                                                     (>evt [::map-moved (.-map ^js e)])))}]
            :zoom               15
            :propertyGeometries (if-some [geometry (:geometry selected-property)]
                                  {:primaryPropertyGeometry (property-model/geojson-feature geometry)}
                                  {})}]]
         (cond
           point-search-loading?
           [:div.flex.items-center.justify-center.h-full.w-full.lg:w-1-3.lg:ml-2.pt-4
            [:div
             (cc/loading-spinner {:with-overlay false})]]

           selected-result
           [selected-result-view selected-result]

           selected-cluster-results
           [selected-cluster-view selected-cluster-results]

           selected-property
           [selected-property-view selected-property])]))))

(defn- map-view [_tr]
  [search-result-map])

(defn view
  ([]
   (view nil))
  ([{:keys [include-secondary-map-records? combined-map-available? on-toggle-secondary-map-records]}]
   (let [tr              (<sub [:tr])
         visible-filters (<sub [::visible-filters])
         n-results       (<sub [::total-count])
         in-progress?    (<sub [:comm/command-processing? :public-records/search-applications])]
     [:div.flex.flex-col.gap-4
      [pr-common/address-pin-search tr [::selected-or-all-municipality-numbers] ::select-property]
      [pr-common/selected-property-chip tr [::selected-property] ::clear-selected-property]
      [search-filter tr]
      [prui/result-count+switcher tr n-results in-progress?
       :class-name            (if (seq visible-filters) "mt-8 mb-4" "mb-4")
       :selected-view-sub     ::selected-view
       :set-selected-view-evt ::set-selected-view]
      (case (<sub [::selected-view])
        :dashboard-search/list-view
        [list-view tr in-progress?]

        [:div
         (when combined-map-available?
           [:div.flex.justify-end.items-center.mb-4
            [:span.mr-3 (tr [:public-records/include-complaints])]
            (cc/toggle {:id         "public-records-add-complaints-toggle"
                        :size       :sm
                        :class-name "unlabeled-toggle"
                        :labelA     (tr [:false])
                        :labelB     (tr [:true])
                        :aria-label (tr [:public-records/include-complaints])
                        :toggled    include-secondary-map-records?
                        :on-toggle  on-toggle-secondary-map-records})])
         [map-view tr]])])))
